<template>
  <div class="gh-testimonial-sliders-container" v-if="items.length">
    <Swiper
      ref="swiper"
      class="swiper gh-testimonial-sliders"
      :modules="modules"
      :loop="true"
      :autoplay="autoplayEnabled ? autoplay : false"
      :options="mergedSwiperOptions"
      @swiper="handleReady"
      pagination
      navigation
    >
      <SwiperSlide v-for="(item, index) in items" :key="index">
        <slot v-bind:item="item"></slot>
      </SwiperSlide>
    </Swiper>

    <div class="swiper-pagination"></div>

    <div class="swiper-button swiper-button-prev">
      <img
        src="https://d26heqcxf097pb.cloudfront.net/images/app/white-arrow-left.svg"
        alt="Click to slide left"
        width="50"
        height="50"
        loading="lazy"
      />
    </div>

    <div class="swiper-button swiper-button-next">
      <img
        src="https://d26heqcxf097pb.cloudfront.net/images/app/white-arrow-right.svg"
        alt="Click to slide right"
        width="50"
        height="50"
        loading="lazy"
      />
    </div>
  </div>
</template>

<script>
import { Swiper as SwiperClass, Pagination, Autoplay, Navigation } from 'swiper/core'
import getAwesomeSwiper from 'vue-awesome-swiper/dist/exporter'
SwiperClass.use([Navigation, Pagination, Autoplay])
const { Swiper, SwiperSlide } = getAwesomeSwiper(SwiperClass)

import 'swiper/swiper-bundle.css'
import { nextTick } from 'vue'
import _ from 'lodash'

export default {
  name: 'testimonial-sliders',

  props: {
    items: {
      type: Array,
      default: () => []
    },
    autoplay: {
      type: [Boolean, Object],
      default: () => ({
        delay: 3000,
      })
    },
    options: {
      type: Object,
      default: () => ({})
    }
  },

  components: {
    Swiper,
    SwiperSlide
  },

  data() {
    return {
      autoplayEnabled: true,
      modules: [Navigation, Pagination],
      swiperOptions: {
        slidesPerView: 1,
        spaceBetween: 20,
        loop: true,
        autoHeight: true,
        centeredSlides: true,
        autoplay: {
          delay: 3000,
          disableOnInteraction: false
        },
        pagination: {
          el: '.swiper-pagination',
          clickable: true
        },
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev'
        },
        breakpoints: {
          640: {
            slidesPerView: 1,
            spaceBetween: 20
          },
          768: {
            slidesPerView: 2,
            spaceBetween: 30
          },
          1112: {
            slidesPerView: 3,
            spaceBetween: 40
          },
          1500: {
            slidesPerView: 4.5
          }
        }
      }
    }
  },

  mounted() {
    window.addEventListener('resize', this.handleResize)

    this.$nextTick(() => {
      const swiper = this.$refs.swiper?.$swiper
      if (swiper) {
        swiper.updateAutoHeight = this.getPatchedUpdateAutoHeight(swiper)
        swiper.update()
        this.handleResize()
      }
    })

    const swiperContainer = document.querySelector('.gh-testimonial-sliders')
    swiperContainer?.addEventListener('mouseenter', this.pauseAutoplay)
    swiperContainer?.addEventListener('mouseleave', this.resumeAutoplay)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    const swiperContainer = document.querySelector('.gh-testimonial-sliders')
    swiperContainer?.removeEventListener('mouseenter', this.pauseAutoplay)
    swiperContainer?.removeEventListener('mouseleave', this.resumeAutoplay)
  },

  computed: {
    mergedSwiperOptions() {
      return {
        ...this.swiperOptions,
        ...this.options,
        autoplay: {
          ...this.swiperOptions.autoplay,
          ...this.options.autoplay
        },
        pagination: {
          ...this.swiperOptions.pagination,
          ...(this.options.pagination || {})
        },
        navigation: {
          ...this.swiperOptions.navigation,
          ...(this.options.navigation || {})
        },
        breakpoints: {
          ...this.swiperOptions.breakpoints,
          ...(this.options.breakpoints || {})
        }
      }
    }
  },

  methods: {
    handleReady(swiper) {
      this.setCardHeight()
      this.swiperInstance = swiper
      swiper.updateAutoHeight = this.getPatchedUpdateAutoHeight(swiper)

      swiper.update()
    },

    getPatchedUpdateAutoHeight(swiper) {
      return function (speed) {
        if (typeof speed === 'number') {
          this.setTransition(speed)
        } else if (speed === true) {
          this.setTransition(this.params.speed)
        }

        const activeSlides = []

        if (this.params.slidesPerView !== 'auto' && this.params.slidesPerView > 1) {
          if (this.params.centeredSlides) {
            this.visibleSlides?.forEach?.(slide => {
              if (slide) activeSlides.push(slide)
            })
          } else {
            for (let i = 0; i < Math.ceil(this.params.slidesPerView); i++) {
              const index = this.activeIndex + i
              if (index >= this.slides.length) break
              activeSlides.push(this.slides[index])
            }
          }
        } else {
          activeSlides.push(this.slides[this.activeIndex])
        }

        let newHeight = 0
        for (let i = 0; i < activeSlides.length; i++) {
          const slide = activeSlides[i]
          if (slide && slide.offsetHeight > newHeight) {
            newHeight = slide.offsetHeight
          }
        }

        if (newHeight && this.$wrapperEl?.css) {
          this.$wrapperEl.css('height', newHeight + 'px')
        }
      }
    },

    setCardHeight() {
      const slides = document.querySelectorAll('.gh-testimonial-sliders .swiper-slide')
      let height = 'auto'

      if (window.innerWidth >= 768) {
        const highestSlide = _.maxBy(slides, el => el.clientHeight)
        if (highestSlide) height = `${highestSlide.clientHeight}px`
      }

      _.each(slides, el => el.style.height = height)
    },

    handleResize() {
      this.setCardHeight()
      const swiper = _.get(this.$refs, 'swiper.$swiper')
      swiper && swiper.slideNext()
    },

    pauseAutoplay() {
      this.$refs.swiper?.$swiper?.autoplay?.stop()
    },

    resumeAutoplay() {
      if (this.autoplay) {
        this.$refs.swiper?.$swiper?.autoplay?.start()
      }
    }
  },

  watch: {
    autoplayEnabled(value) {
      if (!this.$refs.swiper?.$swiper) return
      value ? this.resumeAutoplay() : this.pauseAutoplay()
    },
  }
}
</script>

<style lang="scss">
.gh-testimonial-sliders-container {
  position: relative;
  width: 100%;
  margin: 0 auto;
  
  .swiper-button-prev:after, .swiper-button-next:after {
    display: none;
  }

  .swiper-button-prev, .swiper-button-next {
    top: 37%;
  }

  .swiper-button {
    width: 50px;
    background: rgba(#000, 0.3);
    padding: 1rem;
    height: 50px;
    border-radius: 100%;
  }

  .swiper-pagination {
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
  }

  .swiper-pagination-bullet {
    width: 11px;
    height: 11px;
    + .swiper-pagination-bullet {
      margin-left: 5px;
    }
  }

  .swiper-pagination-bullet-active {
    background: #085078;
  }

  .swiper-slide {
    &.full-height {
      height: 100% !important;
    }
  }
}
</style>